import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { forwardRef } from 'react';
import { ModalCloseButton, ModalDescription, ModalHeader, ModalTitle, StyledOverlay } from '../Modal';
import { keyframes, styled } from '../../system';
import { preventDefault } from '../../lib/utils.lib';
import { wrapWithDefaultProps } from '../../lib/wrap-with-default-props';
const slideIn = keyframes({
    from: { transform: '$$transformValue' },
    to: { transform: 'translate3d(0,0,0)' }
});
const slideOut = keyframes({
    from: { transform: 'translate3d(0,0,0)' },
    to: { transform: '$$transformValue' }
});
const StyledContent = styled(DialogPrimitive.Content, {
    w: '$screen',
    h: '$screenH',
    p: '$4',
    bgColor: '$surface',
    shadow: '$xl',
    position: 'fixed',
    zIndex: '$30',
    '@media (prefers-reduced-motion: no-preference)': {
        willChange: 'transform, opacity',
        '&[data-state="open"]': {
            animation: `${slideIn} 150ms cubic-bezier(0.22, 1, 0.36, 1)`
        },
        '&[data-state="closed"]': {
            animation: `${slideOut} 150ms cubic-bezier(0.22, 1, 0.36, 1)`
        }
    },
    '&:focus': { outline: 'none' },
    variants: {
        side: {
            top: {
                $$transformValue: 'translate3d(0,-100%,0)',
                maxH: '$$maxSizeValue',
                maxW: '$full',
                bottom: 'auto',
                top: 0,
                left: 0,
                right: 0
            },
            right: {
                $$transformValue: 'translate3d(100%,0,0)',
                maxW: '$$maxSizeValue',
                maxH: '$full',
                left: 'auto',
                top: 0,
                bottom: 0,
                right: 0
            },
            bottom: {
                $$transformValue: 'translate3d(0,100%,0)',
                maxH: '$$maxSizeValue',
                maxW: '$full',
                top: 'auto',
                left: 0,
                right: 0,
                bottom: 0
            },
            left: {
                $$transformValue: 'translate3d(-100%,0,0)',
                maxW: '$$maxSizeValue',
                maxH: '$full',
                right: 'auto',
                left: 0,
                top: 0,
                bottom: 0
            }
        }
    },
    defaultVariants: {
        side: 'right'
    }
});
export const DrawerContent = forwardRef(({ children, maxSize, noBackdrop = false, onOpenAutoFocus = preventDefault, ...props }, ref) => (_jsxs(DialogPrimitive.Portal, { children: [_jsx(StyledOverlay, { invisible: noBackdrop }), _jsx(StyledContent, { ref: ref, ...props, css: {
                ...props.css,
                $maxSizeValue: maxSize !== null && maxSize !== void 0 ? maxSize : '300px'
            }, onOpenAutoFocus: onOpenAutoFocus, children: children })] })));
export const Drawer = styled(DialogPrimitive.Root, {});
export const DrawerBody = styled('div', {});
export const DrawerTrigger = wrapWithDefaultProps(styled(DialogPrimitive.Trigger, {}), { asChild: true });
export const DrawerHeader = styled(ModalHeader, {});
export const DrawerCloseTrigger = styled(DialogPrimitive.Close, {});
export const DrawerTitle = styled(ModalTitle, {});
export const DrawerDescription = styled(ModalDescription, {});
export const DrawerCloseButton = styled(ModalCloseButton, {});
Drawer.displayName = 'Drawer';
DrawerTrigger.displayName = 'DrawerTrigger';
DrawerHeader.displayName = 'DrawerHeader';
DrawerBody.displayName = 'DrawerBody';
DrawerContent.displayName = 'DrawerContent';
DrawerCloseTrigger.displayName = 'DrawerCloseTrigger';
DrawerTitle.displayName = 'DrawerTitle';
DrawerDescription.displayName = 'DrawerDescription';
DrawerCloseButton.displayName = 'DrawerCloseButton';
